import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import { constantsData, selectionsData } from "@/data";
import { getClientsListQuery } from "@/graph";
import { openAlert, setSelection } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useClientsList = (
    paginated = true,
    hasAutoCompleteMenu = false,
    searchValue,
    isDrawer = false,
    distributionType,
) => {
    const [fetched, setFetched] = useState(false);

    const [params] = useSearchParams();

    const pageParam = isDrawer ? 1 : parseInt(params.get("page")) || 1;

    const limitParam = isDrawer ? 25 : parseInt(params.get("limit")) || 25;

    const [page, setPage] = useState(pageParam);

    const [limit, setLimit] = useState(limitParam);

    const dispatchedDataRef = useRef(null);

    const dispatch = useDispatch();

    const selections = useSelector((state) => state.selectionsReducer);

    const { isAuthenticated } = useAuth0();

    const { t } = useTranslation();

    const { clients: clientsKey, videoDistributionClients: videoDistributionClientsKey } = selectionsData.keys;

    // a distributionType filter targets a dedicated selection key so it never clobbers the
    // unfiltered clients list other parts of the app rely on
    const selectionKey = distributionType ? videoDistributionClientsKey : clientsKey;

    const { data, error, loading, refetch } = useQuery(getClientsListQuery, {
        skip: !isAuthenticated,
        variables: {
            ...(paginated && {
                limit,
                page: page - 1,
            }),
            ...(searchValue && {
                where: {
                    name: { contains: searchValue },
                },
            }),
            ...(distributionType && { distributionType }),
        },
    });

    if (data?.listClients && dispatchedDataRef.current !== data) {
        dispatchedDataRef.current = data;
        if (paginated && !hasAutoCompleteMenu) setFetched(true);
        else {
            const fetchedList = data.listClients.clients?.map(({ id, isApple, name }) => ({
                isApple: !!isApple,
                label: name,
                value: id,
            }));

            dispatch(
                setSelection({
                    [selectionKey]:
                        selections?.[selectionKey]?.length > 0 &&
                        hasAutoCompleteMenu &&
                        data?.listClients?.meta?.currentPage > 0
                            ? selections?.[selectionKey].concat(fetchedList)
                            : fetchedList,
                }),
            );

            setFetched(true);
        }
    }

    useEffect(
        () => {
            if (error?.message === constantsData.failedToFetch && !loading) {
                displayErrorHandler(t("messages.networkFailure"), dispatch, openAlert);
            }
        },
        [error, data], // eslint-disable-line
    );

    return {
        count: data?.listClients?.meta?.nodeCount,
        data: data?.listClients?.clients,
        error,
        fetched,
        limit,
        loading,
        page: data?.listClients?.meta?.currentPage,
        refetch,
        searchKey: "name",
        setFetched,
        setLimit,
        setPage,
    };
};

export default useClientsList;
